// Render-error boundary; routes/__root.tsx uses it through components/PageErrorBoundary. Promoted from the venue module's kit (src/modules/venue-port).
import React from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from './Button';

/**
 * Default fallback: a TKT panel with the failure, a short reason and Try again.
 * @param {{ error: Error|null, onRetry: () => void, title?: string, description?: string, variant?: 'page'|'inline' }} props
 */
export const ErrorFallback = ({ error, onRetry, title = 'This view could not load', description = 'Something went wrong while showing this part of the page. Your saved work is not affected.', variant = 'page' }) => (
  <div role="alert" className={`bg-white border border-[#E6E4E1] rounded-[10px] flex items-start gap-3 ${variant === 'page' ? 'px-5 py-5 max-w-[560px] mx-auto my-10' : 'px-4 py-3'}`}>
    <span className="w-8 h-8 rounded-full bg-[#FFF7E6] border border-[#FAE2A8] flex items-center justify-center flex-shrink-0">
      <AlertTriangle className="w-4 h-4 text-[#8A5A12]" strokeWidth={1.9} aria-hidden="true" />
    </span>
    <div className="min-w-0 flex-1">
      <div className="text-[13px] font-semibold text-[#1F2937]">{title}</div>
      <div className="text-[12px] text-[#6B7280] mt-0.5 leading-relaxed">{description}</div>
      {error && error.message && <div className="mt-2 text-[11px] font-mono text-[#9B1F1F] break-words">{error.message}</div>}
      <div className="mt-3"><Button type="button" size="sm" variant="secondary" icon={RotateCcw} onClick={onRetry}>Try again</Button></div>
    </div>
  </div>
);

/**
 * Catches render errors below it so one failing view (an engine, a canvas)
 * never blanks the Back Office shell. Retry remounts the children; a change
 * in `resetKeys` (route id, venue id) resets it too.
 *
 *   <ErrorBoundary resetKeys={[venueId]} title="The canvas could not load"><VenueCanvasPage /></ErrorBoundary>
 *
 * @extends {React.Component<{
 *   children?: React.ReactNode,
 *   fallback?: React.ReactNode | ((error: Error, retry: () => void) => React.ReactNode),
 *   onError?: (error: Error, info: React.ErrorInfo) => void,
 *   onReset?: () => void,
 *   resetKeys?: unknown[],
 *   title?: string,
 *   description?: string,
 *   variant?: 'page'|'inline',
 * }, { error: Error|null }>}
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.retry = this.retry.bind(this);
  }

  static getDerivedStateFromError(error) { return { error }; }

  componentDidCatch(error, info) {
    // Surface for debugging; the boundary has already rendered the fallback.
    console.error('[ErrorBoundary]', error?.message || error, info?.componentStack?.split('\n').slice(0, 4).join(' | '));
    if (this.props.onError) this.props.onError(error, info);
  }

  componentDidUpdate(prev) {
    if (!this.state.error) return;
    const a = prev.resetKeys || [], b = this.props.resetKeys || [];
    if (a.length !== b.length || a.some((k, i) => !Object.is(k, b[i]))) this.retry();
  }

  retry() {
    if (this.props.onReset) this.props.onReset();
    this.setState({ error: null });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const { fallback, title, description, variant } = this.props;
    if (typeof fallback === 'function') return fallback(error, this.retry);
    if (fallback !== undefined) return fallback;
    return <ErrorFallback error={error} onRetry={this.retry} title={title} description={description} variant={variant} />;
  }
}
